import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export const useNotifications = (user) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('organization_id')
        ?.eq('id', user?.id)
        ?.single();

      if (!profile?.organization_id) return;

      const orgId = profile?.organization_id;
      const now = new Date();
      const in7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      const nowISO = now?.toISOString();
      const in7DaysISO = in7Days?.toISOString();

      const items = [];

      // 1. Upcoming permit deadlines (within 7 days)
      const { data: permits } = await supabase
        ?.from('permits')
        ?.select('id, project_name, permit_type, deadline, status')
        ?.eq('organization_id', orgId)
        ?.gte('deadline', nowISO)
        ?.lte('deadline', in7DaysISO)
        ?.neq('status', 'approved')
        ?.order('deadline', { ascending: true })
        ?.limit(10);

      (permits || [])?.forEach(permit => {
        const daysLeft = Math.ceil((new Date(permit?.deadline) - now) / (1000 * 60 * 60 * 24));
        items?.push({
          id: `permit-${permit?.id}`,
          type: 'permit',
          title: `Permit Deadline: ${permit?.permit_type}`,
          message: `${permit?.project_name} — ${daysLeft === 0 ? 'Due today' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}`,
          urgency: daysLeft <= 1 ? 'high' : daysLeft <= 3 ? 'medium' : 'low',
          date: permit?.deadline,
          icon: 'FileCheck',
          path: '/permits'
        });
      });

      // 2. Upcoming calendar events (within 7 days)
      const { data: calEvents } = await supabase
        ?.from('calendar_events')
        ?.select('id, title, event_type, start_date')
        ?.eq('organization_id', orgId)
        ?.gte('start_date', nowISO)
        ?.lte('start_date', in7DaysISO)
        ?.order('start_date', { ascending: true })
        ?.limit(10);

      (calEvents || [])?.forEach(event => {
        const daysLeft = Math.ceil((new Date(event?.start_date) - now) / (1000 * 60 * 60 * 24));
        const typeLabels = {
          availability: 'Availability',
          booking: 'Booking',
          milestone: 'Milestone',
          inspection: 'Inspection'
        };
        items?.push({
          id: `event-${event?.id}`,
          type: 'calendar',
          title: event?.title,
          message: `${typeLabels?.[event?.event_type] || 'Event'} — ${daysLeft === 0 ? 'Today' : `in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`}`,
          urgency: daysLeft <= 1 ? 'high' : daysLeft <= 3 ? 'medium' : 'low',
          date: event?.start_date,
          icon: 'Calendar',
          path: '/calendar'
        });
      });

      // 3. Projects with upcoming due dates (within 7 days)
      const { data: projects } = await supabase
        ?.from('projects')
        ?.select('id, customer_name, address, next_milestone')
        ?.eq('organization_id', orgId)
        ?.not('next_milestone', 'is', null)
        ?.limit(5);

      // Sort by most recently updated and flag active ones
      (projects || [])?.forEach(project => {
        if (project?.next_milestone) {
          items?.push({
            id: `project-${project?.id}`,
            type: 'project',
            title: `Milestone: ${project?.next_milestone}`,
            message: `${project?.customer_name || project?.address}`,
            urgency: 'low',
            date: null,
            icon: 'Briefcase',
            path: '/projects'
          });
        }
      });

      // Sort by urgency then date
      const urgencyOrder = { high: 0, medium: 1, low: 2 };
      items?.sort((a, b) => {
        const urgencyDiff = (urgencyOrder?.[a?.urgency] || 2) - (urgencyOrder?.[b?.urgency] || 2);
        if (urgencyDiff !== 0) return urgencyDiff;
        if (a?.date && b?.date) return new Date(a?.date) - new Date(b?.date);
        return 0;
      });

      setNotifications(items?.slice(0, 15));
      setUnreadCount(items?.filter(n => n?.urgency === 'high' || n?.urgency === 'medium')?.length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    // Refresh every 5 minutes
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  return { notifications, unreadCount, loading, refresh: fetchNotifications };
};
